'use client';

import type { ComponentPropsWithoutRef, ElementType, ReactNode, Ref } from 'react';
import { BlockRootStamped, useBlockControlStamp, useBlockRootAttrs } from './blockIdentity';

/**
 * Root element for a site-builder block. Renders {@code as} (default
 * {@code <section>}) and stamps the {@code data-<ns>-group} /
 * {@code data-<ns>-block} / {@code data-<ns>-id} / {@code data-<ns>-anchor}
 * attributes the renderer placed in context — the shared hook for test
 * automation and product onboarding, with <em>no extra DOM node</em>.
 *
 * <p>Blocks built on {@code SectionShell} (blocks package) or
 * {@code AkSection} (akademi-net theme) get this for free. A block with a
 * bespoke outer element swaps its literal {@code <section>} / {@code <div>}
 * for {@code <BlockRoot as="section">} (or calls {@link useBlockRootAttrs}
 * directly and spreads the result).
 *
 * <p>Outside the portal renderer (Storybook, unit tests) the context is
 * empty and this renders a plain element with no data attributes.
 */
export type BlockRootProps<T extends ElementType = 'section'> = {
  /** Host element to render. Defaults to {@code 'section'}. */
  as?: T;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>;

export function BlockRoot<T extends ElementType = 'section'>({
  as,
  children,
  ...rest
}: BlockRootProps<T>) {
  const Tag = (as ?? 'section') as ElementType;
  const rootAttrs = useBlockRootAttrs();
  const ref = useBlockControlStamp<HTMLElement>();
  return (
    <Tag {...rootAttrs} {...rest} ref={ref as Ref<HTMLElement>}>
      <BlockRootStamped>{children}</BlockRootStamped>
    </Tag>
  );
}
